import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, {
  useCallback, useEffect, useMemo, useReducer, useRef, useState,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useRefetchableFragment, useRelayEnvironment } from 'react-relay';

import DashboardControl from 'src/components/Dashboard/DashboardControl';
import { WARNING_TIMEOUT, showTradeRuleMsg } from 'src/components/Dashboard/DashboardControl/helpers/common';
import LoadingOverlay from 'src/components/Dashboard/LoadingOverlay/index';
import Placeholder from 'src/components/Dashboard/Placeholder';
import {
  buildChartViewKey,
  buildEntityShowState,
  buildStateURLParams,
  navigateToEntityShowState,
} from 'src/components/Dashboard/helpers/common';
import Loading from 'src/components/Loading';
import { usePropertyDatespan } from 'src/components/Property/PropertyDatespanProvider';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';
import Map from 'src/enosikit/components/Map';
import FlashesStore from 'src/stores/FlashesStore';
import getFullAddressString from 'src/util/address';
import {
  DATA_AGGREGATE_BY_PROPERTY, DATA_GROUP_BY_COUNTERPARTY, DATA_GROUP_BY_TRADE_TYPE,
  DEFAULT_MAX_VISIBLE_CARDS_COUNT, P1D, PT30M, SOURCE, SOURCE_TRADES, TIME_ZONE_SYSTEM,
  UNIT, UNIT_CURRENCY, UNIT_ENERGY, VIEW_SELECTOR, X_AXIS_DOMAIN_FIXED,
} from 'src/util/constants';
import BrowserProtocol from 'src/util/history';
import { getDefaultLocale } from 'src/util/i18n/handler';
import logger from 'src/util/logger';
import {
  dateRangeToTimeRange,
  getDashboardTimeScope,
  timeInZoneAtStartOfDay,
  timespanChanged,
} from 'src/util/time';

import PropertyShowTradeEnergy from './PropertyShowTradeEnergy';
import { buildMainData } from './data/mainDataBuilder';
import { CHECKLIST_STATES, STATE_METADATA, buildLoadingOverlayState } from './helpers/buildLoadingOverlayState';
import { chartViewSelector, downloadManager } from './helpers/propertyShow';
import usePropertyData from './hooks/usePropertyData';
import {
  PropertyShowLoadingActions,
  PropertyShowLoadingStates,
  defaultInitialState,
} from './reducer/constants';
import loadingStateReducer from './reducer/propertyShowLoadingReducer';

// Static property data
const PropertyShowQuery = graphql`
fragment PropertyShow on Query
@refetchable(queryName: "PropertyShowRefetchQuery")
@argumentDefinitions(
  uuid: {type: "ID!"}
) {
  property(uuid:$uuid) {
    uuid
    externalIdentifier
    active { start finish }
    title
    timezone
    publicHolidayRegion
    address {
      line1
      line2
      city
      state
      country
      postcode
    }
  }
}
`;

/**
 * Description
 * @param {any} props
 * @returns {React.ReactComponentElement} - PropertyShow component
 */
function PropertyShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { queryTimezone, match, initialView } = props;
  const [data] = useRefetchableFragment(PropertyShowQuery, props);
  const { property } = data || {};
  const propertyTimezone = property?.timezone || APIConfig().DEFAULT_TIMEZONE || TIME_ZONE_SYSTEM;
  const timezone = queryTimezone || propertyTimezone;

  const { setDatespanStart } = usePropertyDatespan();
  const searchParams = new URLSearchParams(window.location.search);

  // Parse URL parameters and convert ISO strings to Unix timestamps for getDashboardTimeScope
  const parsedStart = DateTime.fromISO(searchParams.get('start'), { zone: timezone });
  const parsedFinish = DateTime.fromISO(searchParams.get('finish'), { zone: timezone });
  const start = parsedStart.isValid ? parsedStart.startOf('day').toSeconds() : null;
  const finish = parsedFinish.isValid ? parsedFinish.plus({ days: 1 }).startOf('day').toSeconds() : null;
  const { s, f, agg } = getDashboardTimeScope(start, finish, timezone);
  const intl = useIntl();
  const [historianAggregation, setHistorianAggregation] = useState(agg);
  const [tradeAggregation, setTradeAggregation] = useState(agg);
  const [datespan, setDatespan] = useState({
    start: s,
    finish: f,
  });

  const [unit, setUnit] = useState(UNIT_ENERGY);
  const [source, setSource] = useState(SOURCE_TRADES);
  const [tradeBtnClicks, setTradeBtnClicks] = useState(0);
  const [viewSelectorOpen, setViewSelectorOpen] = useState(false);
  const [downloadSelectorOpen, setDownloadSelectorOpen] = useState(false);
  const [chartView, setChartView] = useState(initialView);

  // Get Relay environment for lazy loading propertyUsers
  const relayEnvironment = useRelayEnvironment();

  // Loading state reducer for centralized state management
  const [loadingReducerState, dispatchLoadingAction] = useReducer(
    loadingStateReducer,
    defaultInitialState(),
  );
  const { aggregateBy, groupBy, xAxisDomain } = chartView || {};

  const cardSelectedKeysParam = searchParams.get('cardSelectedKeys');
  const selectedKeys = React.useMemo(() => {
    if (!cardSelectedKeysParam) {
      return {};
    }

    const chartViewKey = buildChartViewKey(chartView, source);
    if (!chartViewKey) {
      return {};
    }

    const keysArray = cardSelectedKeysParam.split(',').filter((key) => key.trim() !== '');
    return keysArray.length > 0 ? { [chartViewKey]: keysArray } : {};
  }, [cardSelectedKeysParam, chartView, source]);

  const firstTimeRender = useRef(true);
  // Tracks whether default chartView values have been applied.
  // Used to prevent URL update when defaults are first applied.
  const defaultsAppliedRef = useRef(false);
  // Flag to skip the next URL update (set before applying defaults, cleared after skipping)
  const skipNextURLUpdateRef = useRef(false);
  // Tracks whether the grouping message should be shown.
  // Once true, stays true until overlay closes.
  const showGroupingMessageRef = useRef(false);

  const {
    finalDatespan,
    finalHistorianAggregation,
    finalTradeAggregation,
  } = useMemo(() => {
    const { start: updatedStart, finish: updatedFinish } = dateRangeToTimeRange(datespan, timezone);
    const timespanDuration = updatedFinish.diff(updatedStart);

    let aggregate = PT30M;
    if (timespanDuration.as('days') > 7) {
      aggregate = P1D;
    }

    return {
      finalDatespan: {
        start: updatedStart,
        finish: updatedFinish,
      },
      finalHistorianAggregation: aggregate,
      finalTradeAggregation: aggregate,
    };
  }, [datespan, timezone]);

  const { combinedData } = usePropertyData({
    propertyId: property?.uuid,
    dateSpan: finalDatespan,
    timezone,
    initialData: data,
    historianAggregation: finalHistorianAggregation,
    tradeAggregation: finalTradeAggregation,
    stateDispatchFunc: dispatchLoadingAction,
  });

  const mainData = useMemo(
    () => buildMainData(combinedData?.property || property, datespan),
    [combinedData?.property, property, datespan],
  );

  // Track whether the chart has finished rendering (used to coordinate overlay hiding)
  const isChartReadyRef = useRef(false);

  // Ref to track the onChartReady timeout for cleanup
  const chartReadyTimeoutRef = useRef(null);

  // Derive overlay visibility from loading state.
  // Overlay is visible during loading, RENDERING, and COMPLETE states.
  // IDLE and ERROR are terminal states where overlay is hidden.
  const { IDLE, COMPLETE, ERROR } = PropertyShowLoadingStates;
  const overlayVisible = loadingReducerState.state !== IDLE
    && loadingReducerState.state !== ERROR;

  // loadingInProgress excludes COMPLETE state so that the overlay remains visible
  // during stage 7 to show the completion message, while still allowing the
  // useEffect below to set groupBy and render the chart.
  const loadingInProgress = overlayVisible && loadingReducerState.state !== COMPLETE;

  // On initial page load, apply default chartView settings based on rules count.
  // This runs once when loading completes (overlay becomes hidden) and mainData is populated.
  // We use meters data (hasMetersData) to determine if mainData has been populated.
  const rulesCount = Object.keys(mainData.buy.rules).length
    + Object.keys(mainData.sell.rules).length;
  const hasMetersData = Object.keys(mainData.meters).length > 0;
  useEffect(() => {
    // Skip if it is still loading, or mainData not yet populated.
    if (loadingInProgress || !hasMetersData) {
      return;
    }

    // Skip if defaults have already been applied (prevents re-applying on re-renders)
    if (defaultsAppliedRef.current) {
      return;
    }

    // The maximum possible number of cards from 16 rules is 16 cards. This maximum of 16 cards
    // results in a 4x4 grid layout (4 rows of 4 cards on a normal laptop screen).
    // Beyond 16 rules, we switch to grouping by trade type to avoid potentially too many cards.
    const defaultGroupBy = rulesCount > DEFAULT_MAX_VISIBLE_CARDS_COUNT
      ? DATA_GROUP_BY_TRADE_TYPE
      : DATA_GROUP_BY_COUNTERPARTY;

    // Set flag to skip URL update for this initial default application
    skipNextURLUpdateRef.current = true;

    setChartView((prevView) => ({
      ...prevView,
      groupBy: prevView.groupBy || defaultGroupBy,
      aggregateBy: prevView.aggregateBy || DATA_AGGREGATE_BY_PROPERTY,
      xAxisDomain: prevView.xAxisDomain || X_AXIS_DOMAIN_FIXED,
    }));

    // Mark defaults as applied to prevent re-applying on re-renders
    defaultsAppliedRef.current = true;
  }, [loadingInProgress, hasMetersData, rulesCount]);
  // Update the context whenever datespan.start changes
  useEffect(() => {
    if (datespan?.start) {
      setDatespanStart(datespan.start);
    }
  }, [datespan?.start, setDatespanStart]);

  /**
   * Synchronizes current chart view state (date range, timezone, view settings) to browser URL
   * query parameters using REPLACE navigation to avoid creating new history entries.
   */
  const updateURLState = useCallback(() => {
    const urlState = buildEntityShowState(
      datespan,
      match,
      chartView,
      timezone,
      source,
      unit,
      selectedKeys,
    );

    if (!urlState) {
      return;
    }

    navigateToEntityShowState(urlState);
  }, [datespan, match, chartView, timezone, source, unit, selectedKeys]);

  const doRefetch = () => {
    // Reset chart ready state for new data load
    isChartReadyRef.current = false;

    const { start: updatedStart, finish: updatedFinish } = dateRangeToTimeRange(
      datespan,
      timezone,
    );
    const updatedDateSpan = updatedFinish.diff(updatedStart);

    let aggregate = PT30M;
    if (updatedDateSpan.as('days') > 7) {
      aggregate = P1D;
    }

    setHistorianAggregation(aggregate);
    setTradeAggregation(aggregate);

    const { location } = match;
    const { pathname } = location;

    const state = buildStateURLParams(
      chartView,
      updatedStart,
      updatedFinish.minus({ days: 1 }),
      timezone,
      source,
      unit,
      selectedKeys,
    );

    if (!state) {
      logger.warn('Failed to generate URL state, skipping update');
      return;
    }

    const searchString = Object.keys(state)
      .map((k) => [k, encodeURIComponent(state[k])].join('='))
      .join('&');
    const formattedSearchString = `?${searchString}`;

    BrowserProtocol.navigate({
      action: 'REPLACE',
      pathname,
      search: formattedSearchString,
      hash: '',
      state,
    });
  };

  // Temporary code until we implement updating the overlay message for each batch
  // of meters and trade rules.
  // See the commented-out useEffect below for the intended implementation.
  // See comment in dataBatching.js for more details.
  /**
   * Manages timed state transitions for the loading overlay.
   * Each loading stage displays its completion message for a minimum duration
   * before transitioning to the next stage, ensuring users have time to see
   * progress feedback.
   * - METERS_LOADED: 1 second to show "Loaded X meters"
   * - RULES_LOADED: 1 second to show "Loaded X trade rules"
   * - PREPARING_DATA: 1 second to show "Preparing data"
   * - RENDERING: 1 second to show "Rendering data"
   * - COMPLETE: 1 second to show "Data rendered"
   */
  useEffect(() => {
    let timerId;

    if (loadingReducerState.state === PropertyShowLoadingStates.METERS_LOADED) {
      timerId = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.METERS_DISPLAYED });
      }, 1000);
    } else if (loadingReducerState.state === PropertyShowLoadingStates.RULES_LOADED) {
      timerId = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.RULES_DISPLAYED });
      }, 1000);
    } else if (loadingReducerState.state === PropertyShowLoadingStates.PREPARING_DATA) {
      // PREPARING_DATA stage: Transition to RENDERING after 1 second delay
      timerId = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.DATA_PREPARED });
      }, 1000);
    } else if (loadingReducerState.state === PropertyShowLoadingStates.RENDERING) {
      // RENDERING stage: Transition depends on whether the chart can/has rendered.
      // The chart only renders when groupBy is set (see line 544)
      if (isChartReadyRef.current) {
        // Case 1: Chart already finished rendering. Proceed after 1 second delay.
        timerId = setTimeout(() => {
          dispatchLoadingAction({ type: PropertyShowLoadingActions.RENDERING_DISPLAYED });
        }, 1000);
      } else if (!groupBy) {
        // Case 2: groupBy is not set (e.g., initial load without URL params).
        // The chart component won't mount, so onChartReady will never fire.
        // Use a timeout fallback to prevent the overlay from getting stuck.
        timerId = setTimeout(() => {
          dispatchLoadingAction({ type: PropertyShowLoadingActions.RENDERING_DISPLAYED });
        }, 1000);
      }
      // Case 3 (implicit else): groupBy exists but chart hasn't finished yet.
      // No action here - the onChartReady callback (see below) will trigger the transition.
    }

    if (loadingReducerState.state === PropertyShowLoadingStates.COMPLETE) {
      timerId = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.HIDE_OVERLAY });
      }, 1000);
    }

    return () => clearTimeout(timerId);
  }, [loadingReducerState.state, groupBy]);

  /** Callback invoked when the chart has completed its initial render. */
  const onChartReady = useCallback(() => {
    isChartReadyRef.current = true;
    // Wait 1 second after chart is ready to let users read Stage 8 message,
    // then transition to COMPLETE
    if (loadingReducerState.state === PropertyShowLoadingStates.RENDERING) {
      // Clear any existing timeout to prevent duplicate dispatches
      if (chartReadyTimeoutRef.current) {
        clearTimeout(chartReadyTimeoutRef.current);
      }
      chartReadyTimeoutRef.current = setTimeout(() => {
        dispatchLoadingAction({ type: PropertyShowLoadingActions.RENDERING_DISPLAYED });
        chartReadyTimeoutRef.current = null;
      }, 1000);
    }
  }, [loadingReducerState.state]);

  // Cleanup the onChartReady timeout on unmount
  useEffect(() => () => {
    if (chartReadyTimeoutRef.current) {
      clearTimeout(chartReadyTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!firstTimeRender.current) {
      doRefetch();
    }
  }, [datespan]);

  useEffect(() => {
    // Skip URL update on initial load to keep the URL clean (no query params).
    // This allows us to distinguish fresh page loads from subsequent UI renders or interactions.
    // When URL has no params, we know it's a fresh load and can show the grouping message
    // (groupBy Counterparty or TradeType) to guide the user.
    // Note: These values (tz, start, finish, etc.) still exist in component state and are used
    // for data transformations - they're just not reflected in the URL on initial load.
    // URL params are only appended after user interactions (date change, view change, etc.).
    if (firstTimeRender.current) {
      return;
    }

    // Skip URL update when defaults are being applied (not user-initiated)
    if (skipNextURLUpdateRef.current) {
      skipNextURLUpdateRef.current = false;
      return;
    }

    updateURLState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aggregateBy, groupBy, xAxisDomain, source, unit]);

  useEffect(() => {
    firstTimeRender.current = false;
  }, []);

  // Manage showGroupingMessageRef based on loading state and groupBy
  useEffect(() => {
    const currentStateOrder = STATE_METADATA[loadingReducerState.state]?.completedUpTo ?? 0;

    // Start showing the message when we reach RULES stage and groupBy is not set from URL
    const reachedRulesStage = currentStateOrder >= CHECKLIST_STATES.RULES;
    if (reachedRulesStage && !groupBy && !showGroupingMessageRef.current) {
      showGroupingMessageRef.current = true;
    }

    // Reset the ref when overlay closes (state becomes IDLE)
    if (loadingReducerState.state === IDLE) {
      showGroupingMessageRef.current = false;
    }
  }, [loadingReducerState.state, groupBy]);

  /** Toggles the view or download selector dropdown. */
  const toggle = useCallback((dropdownType) => {
    if (dropdownType === VIEW_SELECTOR) {
      setViewSelectorOpen((prev) => !prev);
    } else {
      setDownloadSelectorOpen((prev) => !prev);
    }
  }, []);

  const controlSetStateFunc = useCallback((opts) => {
    const { group, value } = opts;

    let chartSource = source;
    let chartUnit = unit;
    let btnClicks = tradeBtnClicks;

    FlashesStore.reset();

    if (value === chartSource && chartSource === SOURCE_TRADES) {
      if (btnClicks === 0) {
        clearTimeout();
        btnClicks += 1;
      } else {
        showTradeRuleMsg(intl);
      }
      setTimeout(() => {
        setTradeBtnClicks(0);
      }, WARNING_TIMEOUT);
    }

    if (group === UNIT) {
      chartUnit = value;
    }

    if (group === SOURCE) {
      chartSource = value;
      if (chartUnit === UNIT_CURRENCY) {
        chartUnit = UNIT_ENERGY;
      }
    }

    setSource(chartSource);
    setUnit(chartUnit);
    setTradeBtnClicks(btnClicks);
  }, [source, unit, tradeBtnClicks, intl]);

  /** Handles timespan updates from the date picker. */
  const handleTimespanUpdate = useCallback((updatedStart, updatedFinish) => {
    const inputStart = timeInZoneAtStartOfDay(updatedStart, timezone);
    const inputFinish = timeInZoneAtStartOfDay(updatedFinish, timezone);
    const currentTimespan = {
      start: timeInZoneAtStartOfDay(datespan.start, timezone),
      finish: timeInZoneAtStartOfDay(datespan.finish, timezone),
    };

    const isTimespanChanged = timespanChanged(inputStart, inputFinish, currentTimespan);
    if (!isTimespanChanged) {
      return;
    }

    setDatespan({ start: updatedStart, finish: updatedFinish });
    dispatchLoadingAction({ type: PropertyShowLoadingActions.START_REQUEST });
  }, [timezone, datespan.start, datespan.finish]);

  const { address, title } = property;
  const addr = getFullAddressString(address);

  if (!mainData) {
    const pageError = intl.formatMessage({
      id: 'property.property_show.page.error',
      defaultMessage: 'Data unable to be processed.',
    });
    FlashesStore.flash(FlashesStore.ERROR, pageError);
    return null;
  }
  const pageTitle = intl.formatMessage({ id: 'property.property_show.page.title', defaultMessage: 'Enosi - Property: {propertyTitle}' }, { propertyTitle: title });

  // Determines whether to display a message informing the user about the automatic
  // chart grouping selection.
  // Show the grouping message on every hard refresh/page load while data is loading.
  // Once the message starts showing (stage >= RULES and no groupBy from URL), it stays
  // visible until the overlay closes. This prevents the message from disappearing when
  // defaults are applied (which sets groupBy).
  // Skip the message if URL contains groupBy parameter (user's explicit grouping choice).
  const showGroupingMessage = overlayVisible && showGroupingMessageRef.current;
  const { loadingOverlayProps } = buildLoadingOverlayState({
    intl,
    datespan,
    timezone,
    loadingState: loadingReducerState,
    showGroupingMessage,
  });

  /** Timespan with locale and timezone applied for DashboardControl. */
  const dateSpanWithLocaleAndTimezone = useMemo(() => {
    const { start: startTime, finish: finishTime } = datespan;
    const locale = getDefaultLocale();
    return {
      start: startTime.setLocale(locale).setZone(timezone),
      finish: finishTime.setLocale(locale).setZone(timezone),
    };
  }, [datespan, timezone]);

  /** Date range converted to time range for PropertyShowTradeEnergy. */
  const timeRangeForChart = useMemo(
    () => dateRangeToTimeRange(datespan, timezone),
    [datespan, timezone],
  );

  /**
   * Download handler for DataPack and CSV downloads.
   * For DataPack, propertyUsers is fetched before generating the download.
   */
  const handleDownload = useCallback(
    (type, downloadData, dateRange, intlArg) => {
      downloadManager(type, downloadData, dateRange, intlArg, relayEnvironment, property?.uuid);
    },
    [relayEnvironment, property?.uuid],
  );

  /** Renders the dashboard control bar with chart controls and download options. */
  const renderPropertyControls = useCallback((propertyShowData) => (
    <DashboardControl
      chartView={chartView}
      chartViewSelector={chartViewSelector}
      controlSetStateFunc={controlSetStateFunc}
      downloadFunc={handleDownload}
      downloadSelectorOpen={downloadSelectorOpen}
      isModalOpen={overlayVisible}
      mainData={propertyShowData}
      timespan={dateSpanWithLocaleAndTimezone}
      timespanUpdateFunc={handleTimespanUpdate}
      timezone={timezone}
      setChartViewFunc={setChartView}
      source={source}
      toggleFunc={toggle}
      viewSelectorOpen={viewSelectorOpen}
      unit={unit}
    />
  ), [
    chartView, controlSetStateFunc, dateSpanWithLocaleAndTimezone, downloadSelectorOpen,
    handleDownload, handleTimespanUpdate, overlayVisible, source,
    timezone, toggle, unit, viewSelectorOpen,
  ]);

  const breadcrumbLabel = intl.formatMessage({ id: 'property.property_show.breadcrumbs.properties.label', defaultMessage: ' Properties' });
  const overviewTitle = intl.formatMessage({ id: 'property.property_show.heading.overview.title', defaultMessage: 'Overview' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>
      <Breadcrumbs breadcrumbs={[{
        name: breadcrumbLabel, path: '/properties',
      }, { name: title }]}
      />
      <Heading
        controls={renderPropertyControls(mainData)}
        context={overviewTitle}
        subtitle={addr}
        title={title}
      />
      {overlayVisible && (
        <>
          <LoadingOverlay inputs={loadingOverlayProps} />
          <Placeholder />
        </>
      )}
      {groupBy && (
        <div style={overlayVisible ? { visibility: 'hidden', position: 'absolute' } : {}}>
          <PropertyShowTradeEnergy
            controlSetStateFunc={controlSetStateFunc}
            chartView={chartView}
            handleTimespanUpdate={handleTimespanUpdate}
            mainData={mainData}
            historianAggregation={historianAggregation}
            onChartReadyFunc={onChartReady}
            source={source}
            tradeAggregation={tradeAggregation}
            timespan={timeRangeForChart}
            timezone={timezone}
            unit={unit}
          />
          {address && <Map address={address} />}
        </div>
      )}
    </>
  );
}

export default PropertyShow;

PropertyShow.propTypes = {
  match: PropTypes.shape({
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  relay: PropTypes.shape({
    refetch: PropTypes.func,
  }),
  queryTimezone: PropTypes.string,
  initialView: PropTypes.shape({
    aggregateBy: PropTypes.string,
    groupBy: PropTypes.string,
    xAxisDomain: PropTypes.string,
  }).isRequired,
};

PropertyShow.defaultProps = {
  relay: undefined,
  queryTimezone: undefined,
};
